import { ReactNode } from 'react';
import { Row } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import AirbnbIcon from 'assets/images/icons/channels/airbnb.svg?react';
import BookingDotComIcon from 'assets/images/icons/channels/booking.svg?react';
import HostfullyIcon from 'assets/images/icons/channels/hostfully.svg?react';
import HvmbIcon from 'assets/images/icons/channels/hvmi.svg?react';
import VrboIcon from 'assets/images/icons/channels/vrbo.svg?react';
import useFetchPropertyChannelSettings from 'components/domain/channelSettings/useFetchPropertyChannelSettings';
import LoadingPlaceholder from 'components/loader/LoadingPlaceholder';
import Tooltip from 'components/tooltip/Tooltip';
import { Channel } from 'models/Channels';
import { PropertyInternalFee } from 'models/PropertyFees';
import { ChannelCellStyled } from './ChannelCell.styles';

interface ChannelCellProps {
  row: Row<PropertyInternalFee>;
}

const ChannelIcon = ({
  channel,
  children,
  showTooltip,
  uid,
}: {
  channel: Channel;
  children: ReactNode;
  showTooltip: boolean;
  uid: string;
}) => {
  const { t } = useTranslation();

  if (showTooltip) {
    return (
      <Tooltip
        id={`${uid}-channel-${channel}`}
        text={t(
          'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.channelNotAvailableTooltip',
        )}
      >
        {children}
      </Tooltip>
    );
  }

  return children;
};

const ChannelCell = ({ row }: ChannelCellProps) => {
  const { uid: propertyUid } = useParams();
  const {
    channelSettings: propertyChannelSettings,
    isFetching: isFetchingPropertyChannelSettings,
  } = useFetchPropertyChannelSettings(propertyUid!);

  const activeChannelsAtPropertyLevel = (propertyChannelSettings ?? [])
    .filter(({ isActive }) => isActive)
    .map(({ enumId }) => enumId);

  const isHostfullyActiveAtPropertyLevel =
    activeChannelsAtPropertyLevel.includes(Channel.orbirental);

  const isAirbnbActiveAtPropertyLevel = activeChannelsAtPropertyLevel.includes(
    Channel.airbnb,
  );

  const isBookingDotComActiveAtPropertyLevel =
    activeChannelsAtPropertyLevel.includes(Channel.booking_com);

  const isVrboActiveAtPropertyLevel = activeChannelsAtPropertyLevel.includes(
    Channel.homeaway,
  );

  const isHvmbActiveAtPropertyLevel = activeChannelsAtPropertyLevel.includes(
    Channel.hvmi,
  );

  const {
    airbnbType,
    bookingDotComType,
    hostfullyType,
    hvmiType,
    vrboType,
    uid,
  } = row.original;

  if (isFetchingPropertyChannelSettings) {
    return <LoadingPlaceholder />;
  }

  return (
    <ChannelCellStyled>
      {hostfullyType && (
        <ChannelIcon
          channel={Channel.orbirental}
          showTooltip={!isHostfullyActiveAtPropertyLevel}
          uid={uid}
        >
          <HostfullyIcon
            className={isHostfullyActiveAtPropertyLevel ? 'is-active' : ''}
            data-testid="hostfully-icon"
          />
        </ChannelIcon>
      )}

      {airbnbType && (
        <ChannelIcon
          channel={Channel.airbnb}
          showTooltip={!isAirbnbActiveAtPropertyLevel}
          uid={uid}
        >
          <AirbnbIcon
            className={isAirbnbActiveAtPropertyLevel ? 'is-active' : ''}
            data-testid="airbnb-icon"
          />
        </ChannelIcon>
      )}

      {bookingDotComType && (
        <ChannelIcon
          channel={Channel.booking_com}
          showTooltip={!isBookingDotComActiveAtPropertyLevel}
          uid={uid}
        >
          <BookingDotComIcon
            className={isBookingDotComActiveAtPropertyLevel ? 'is-active' : ''}
            data-testid="bcom-icon"
          />
        </ChannelIcon>
      )}

      {vrboType && (
        <ChannelIcon
          channel={Channel.homeaway}
          showTooltip={!isVrboActiveAtPropertyLevel}
          uid={uid}
        >
          <VrboIcon
            className={isVrboActiveAtPropertyLevel ? 'is-active' : ''}
            data-testid="vrbo-icon"
          />
        </ChannelIcon>
      )}

      {hvmiType && (
        <ChannelIcon
          channel={Channel.hvmi}
          showTooltip={!isHvmbActiveAtPropertyLevel}
          uid={uid}
        >
          <HvmbIcon
            className={isHvmbActiveAtPropertyLevel ? 'is-active' : ''}
            data-testid="hvmb-icon"
          />
        </ChannelIcon>
      )}
    </ChannelCellStyled>
  );
};

export default ChannelCell;
